import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RaceHeader, useWidgetAnchor } from '@swf/casino';
import { CacheControl, Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import Translation from '@swf/components/Translation/Translation';
import { Header } from '@swf/core/enums';
import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import type { CasinoRace, RaceFilter } from '@swf/core/types/CasinoRaces';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle, isCompleted } from '@swf/core/utils/casino';

import {
  TXTWCL_We_are_out_of_fuel,
  TXTWCL_We_are_taking_a_short_stop_to__ELL
} from './CasinoRacesWidget.messages.json';
import styles from './CasinoRacesWidget.scss';
import CompletedGrid from './components/CompletedGrid/CompletedGrid';
import RacesGrid from './components/RacesGrid/RacesGrid';
import { racesWidgetModel, useRacesWidgetModel } from './model';

const racesEmpty = (races: CasinoRace[]) =>
  !(races?.length > 0) || races.every((race) => isCompleted(race?.status));

const CasinoRacesWidget: WidgetFC = () => {
  const scrollOffset = useRef<number>(0);
  const { modal } = useDispatch<Dispatch>();
  const { widgetId } = useWidget();
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const { widgetDispatch, widgetSelect, widgetConfig } = useRacesWidgetModel();
  const isMyRaces = useSelector(widgetSelect.isMyRaces);
  const data = useSelector(widgetSelect.get);
  const { races } = data;
  const widgetTargetId = useWidgetAnchor();

  const showLeaderboard = (racePath: string) => async () => {
    scrollOffset.current = window.pageYOffset;
    modal.enqueue({
      path: '/casino/race-leaderboard',
      locationState: { widgetId, path: racePath }
    });
  };

  const handleFilter = ({ tag }: RaceFilter) => {
    widgetDispatch.setActive(tag.id);
  };

  const isLoaded = !useSelector(widgetSelect.isLoading);

  const {
    layout,
    logo,
    help,
    help_modal_button: helpModalButton,
    help_title: helpTitle,
    more_link: moreLink = ''
  } = widgetConfig;

  const completed = (useSelector(widgetSelect.getCompleted) || []) as CasinoRace[];

  return (
    <section className={styles.container} style={getStyle(layout)} id={widgetTargetId}>
      <CacheControl>public, max-age=600</CacheControl>
      <Vary by={[Header.Currency]} />
      <RaceHeader
        logo={logo}
        help={help}
        helpButton={helpModalButton}
        helpTitle={helpTitle}
        moreLink={moreLink}
        active={data.active}
        showFilters={isAuthenticated && widgetConfig.user.config.logged_in_only}
        handleFilter={handleFilter}
      />
      {races && racesEmpty(races) && !isMyRaces && isLoaded ? (
        <div className={styles.noRaces}>
          <span className={styles.warningTitle}>
            <Translation {...TXTWCL_We_are_out_of_fuel} />
          </span>
          <span className={styles.warningContext}>
            <Translation {...TXTWCL_We_are_taking_a_short_stop_to__ELL} />
          </span>
        </div>
      ) : (
        <RacesGrid
          races={races || []}
          onLeaderboard={showLeaderboard}
          leaderboardVisible={widgetConfig.leaderboard_visible}
          widgetId={widgetId}
          path="races"
        />
      )}
      {isMyRaces && isAuthenticated && (
        <CompletedGrid
          path="user_completed"
          races={completed}
          fetchCompleted={widgetDispatch.fetchCompleted}
          showLeaderboard={showLeaderboard}
        />
      )}
    </section>
  );
};

CasinoRacesWidget.model = racesWidgetModel;
export default CasinoRacesWidget;
